/**
 * useAssistantVoice — Push-to-talk voice assistant hook.
 *
 * Behavior:
 *  - The user PRESSES the assistant button (mousedown/touchstart) → start
 *    recording via MediaRecorder.
 *  - The user RELEASES the button → stop recording and upload the audio
 *    to /api/web/assistant/voice. The server returns a transcript, the
 *    assistant reply text, and the audio of the reply (base64 mp3) which
 *    we play immediately.
 *
 * Premium: backend charges 5 credits per call. If the user has no balance
 * the API returns 402 and we surface a friendly error.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import webApi from "@/lib/webApi";

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return "";
}

export default function useAssistantVoice({ onResult } = {}) {
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0);          // 0..1 mic input level
  const [voice, setVoice] = useState("es-MX-DaliaNeural");
  const [voices, setVoices] = useState([]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const playerRef = useRef(null);
  const recordingRef = useRef(false); // synchronous mirror of `recording`

  // Load available voices once.
  useEffect(() => {
    let alive = true;
    webApi
      .get("/assistant/voices")
      .then((res) => {
        if (!alive) return;
        const v = res?.data?.voices || [];
        setVoices(v);
        if (res?.data?.default) setVoice(res.data.default);
      })
      .catch((err) => {
        console.debug("[useAssistantVoice] voices fetch failed", err);
      });
    return () => {
      alive = false;
    };
  }, []);

  const stopMeter = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setLevel(0);
  }, []);

  const cleanupRecorder = useCallback(() => {
    stopMeter();
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch (e) {
      console.debug("[useAssistantVoice] track stop failed", e);
    }
    streamRef.current = null;
    try {
      audioCtxRef.current?.close();
    } catch (e) {
      console.debug("[useAssistantVoice] audioCtx close failed", e);
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
    chunksRef.current = [];
    mediaRecorderRef.current = null;
  }, [stopMeter]);

  const playReply = useCallback((b64, mime) => {
    if (!b64) return;
    try {
      const bin = atob(b64);
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
      const blob = new Blob([buf], { type: mime || "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      playerRef.current = audio;
      audio.play().catch((err) => {
        console.debug("[useAssistantVoice] audio autoplay blocked", err);
      });
      audio.onended = () => URL.revokeObjectURL(url);
    } catch (e) {
      // Audio playback failure is non-fatal — the transcribed text reply
      // is still rendered to the user. We log for debugging without a UI toast.
      console.debug("[useAssistantVoice] playReply failed", e);
    }
  }, []);

  const sendAudio = useCallback(
    async (blob) => {
      setSending(true);
      setError(null);
      try {
        const fd = new FormData();
        const filename = blob.type.includes("webm") ? "voice.webm" : "voice.audio";
        fd.append("file", blob, filename);
        fd.append("voice", voice);
        const res = await webApi.post("/assistant/voice", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        const d = res?.data || {};
        if (d.audio_base64) playReply(d.audio_base64, d.audio_mime);
        onResult?.(d);
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || "Error";
        if (status === 402) {
          setError("Saldo insuficiente. El asistente cuesta 5 créditos por uso.");
        } else {
          setError(msg);
        }
        onResult?.({ error: msg, status });
      } finally {
        setSending(false);
      }
    },
    [onResult, playReply, voice],
  );

  const start = useCallback(async () => {
    if (recordingRef.current || sending) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Optional VU meter for visual feedback.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyserRef.current = analyser;
          const data = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            if (!analyserRef.current) return;
            analyser.getByteFrequencyData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) sum += data[i];
            const avg = sum / data.length / 255;
            setLevel(Math.min(1, avg * 1.8));
            rafRef.current = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (e) {
        // VU meter is a nice-to-have; failing here (e.g., iOS AudioContext
        // suspended before user gesture) must NOT block the recording flow.
        console.debug("[useAssistantVoice] VU meter setup failed", e);
      }

      const mimeType = pickMimeType();
      const opts = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, opts);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeType || "audio/webm",
        });
        cleanupRecorder();
        recordingRef.current = false;
        setRecording(false);
        // Ignore ultra-short presses (likely accidental taps).
        if (blob.size < 1500) {
          setError("Mantén presionado más tiempo y vuelve a intentar.");
          return;
        }
        sendAudio(blob);
      };
      recorder.start();
      recordingRef.current = true;
      setRecording(true);
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Permiso de micrófono denegado."
          : "No pude acceder al micrófono.",
      );
      cleanupRecorder();
      recordingRef.current = false;
      setRecording(false);
    }
  }, [cleanupRecorder, sendAudio, sending]);

  const stop = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    try {
      if (rec.state !== "inactive") rec.stop();
    } catch (e) {
      console.debug("[useAssistantVoice] recorder.stop() failed", e);
    }
  }, []);

  const cancel = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop();
    } catch (e) {
      console.debug("[useAssistantVoice] cancel stop failed", e);
    }
    chunksRef.current = [];
    cleanupRecorder();
    recordingRef.current = false;
    setRecording(false);
  }, [cleanupRecorder]);

  const stopPlayback = useCallback(() => {
    try {
      playerRef.current?.pause();
    } catch (e) {
      console.debug("[useAssistantVoice] pause failed", e);
    }
    playerRef.current = null;
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      cleanupRecorder();
      stopPlayback();
    };
  }, [cleanupRecorder, stopPlayback]);

  return {
    recording,
    sending,
    error,
    level,
    voice,
    setVoice,
    voices,
    start,
    stop,
    cancel,
    stopPlayback,
  };
}
